import { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Section, Heading } from "../Bits";
import { Lock } from "lucide-react";

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return fallback;
};

export default function AdminLogin({ onLoggedIn }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const input = "w-full px-4 py-3 rounded-xl border";
  const bc = { borderColor: "var(--wp-border)" };

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/auth/login", { email: email.trim(), password });
      setPassword("");
      onLoggedIn();
    } catch (err) {
      toast.error(errText(err, "Anmeldung fehlgeschlagen"));
    } finally {
      setBusy(false);
    }
  };

  const forgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/auth/forgot", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(errText(err, "Bitte gültige E-Mail eingeben"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section>
      <div className="max-w-sm mx-auto plant-card p-8" data-testid="admin-login">
        <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full mb-4" style={{ backgroundColor: "var(--wp-primary)", color: "var(--wp-cream)" }}>
          <Lock className="w-6 h-6" />
        </div>
        <Heading overline="Backoffice" title={mode === "login" ? "Admin-Login" : "Passwort vergessen"} center />
        {mode === "login" ? (
          <form onSubmit={login} className="space-y-3">
            <input type="email" required autoComplete="username" placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} className={input} style={bc} data-testid="admin-login-email" />
            <input type="password" required autoComplete="current-password" placeholder="Passwort" value={password} onChange={(e) => setPassword(e.target.value)} className={input} style={bc} data-testid="admin-login-password" />
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="admin-login-submit">{busy ? "…" : "Anmelden"}</button>
            <button type="button" onClick={() => { setMode("forgot"); setSent(false); }} className="text-sm underline opacity-70 w-full" data-testid="admin-forgot-link">Passwort vergessen / erstes Passwort festlegen</button>
          </form>
        ) : sent ? (
          <div className="space-y-4 text-sm" data-testid="admin-forgot-sent">
            <p>Wenn die Adresse zu einem Backoffice-Konto gehört, ist ein Link unterwegs. Er ist 30 Minuten gültig und funktioniert einmal.</p>
            <button type="button" onClick={() => setMode("login")} className="btn-secondary w-full justify-center" data-testid="admin-back-to-login">Zurück zum Login</button>
          </div>
        ) : (
          <form onSubmit={forgot} className="space-y-3">
            <p className="text-sm opacity-80">Gib deine Backoffice-E-Mail ein. Du bekommst einen Link, mit dem du ein neues Passwort festlegst.</p>
            <input type="email" required placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} className={input} style={bc} data-testid="admin-forgot-email" />
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="admin-forgot-submit">{busy ? "…" : "Link senden"}</button>
            <button type="button" onClick={() => setMode("login")} className="text-sm underline opacity-70 w-full">Zurück zum Login</button>
          </form>
        )}
      </div>
    </Section>
  );
}

export function AdminResetPage() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const input = "w-full px-4 py-3 rounded-xl border";
  const bc = { borderColor: "var(--wp-border)" };

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Die Passwörter stimmen nicht überein");
    setBusy(true);
    try {
      await api.post("/admin/auth/reset", { token, password: pw });
      setDone(true);
    } catch (err) {
      toast.error(errText(err, "Der Link ist ungültig oder abgelaufen"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section>
      <div className="max-w-sm mx-auto plant-card p-8" data-testid="admin-reset">
        <Heading overline="Backoffice" title="Neues Passwort" center />
        {done ? (
          <div className="space-y-4 text-sm text-center" data-testid="admin-reset-done">
            <p>Dein Passwort ist gespeichert. Alle alten Anmeldungen wurden beendet.</p>
            <a href="/admin" className="btn-primary w-full justify-center">Zum Login</a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input type="password" required minLength={10} autoComplete="new-password" placeholder="Neues Passwort (mind. 10 Zeichen)" value={pw} onChange={(e) => setPw(e.target.value)} className={input} style={bc} data-testid="admin-reset-password" />
            <input type="password" required minLength={10} autoComplete="new-password" placeholder="Passwort wiederholen" value={pw2} onChange={(e) => setPw2(e.target.value)} className={input} style={bc} data-testid="admin-reset-password2" />
            <button disabled={busy || !token} className="btn-primary w-full justify-center" data-testid="admin-reset-submit">{busy ? "…" : "Passwort speichern"}</button>
            {!token && <p className="text-xs" style={{ color: "var(--wp-toxic)" }}>Kein gültiger Link – bitte fordere einen neuen an.</p>}
          </form>
        )}
      </div>
    </Section>
  );
}
